/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faVideo, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from './ThemedLoadingSpinner';
import '../styles/AssociationCalendar.css';

const AssociationCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState('all'); // 'all' | 'events' | 'interviews'
  const [hoveredDay, setHoveredDay] = useState(null);

  const formatDateKey = (dateObj) => {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateKey = (dateString) => {
    if (!dateString) return null;
    const [datePart] = (dateString || '').split('T');
    if (!datePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
    return new Date(year, month - 1, day);
  };

  const formatTimeValue = (value) => {
    if (!value) return '';
    if (!value.includes(':')) return value;
    const time = new Date(`1970-01-01T${value}`);
    if (Number.isNaN(time.getTime())) return value;
    return time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const normalizeEvent = (ev) => {
    const datePart = (ev.eventDate || '').toString().split('T')[0];
    return {
      ...ev,
      type: 'event',
      dateKey: datePart,
      localDate: parseDateKey(datePart),
      time: ev.eventTime || '',
      displayTitle: ev.title,
      displaySubtitle: ev.eventType
    };
  };

  const normalizeInterview = (iv) => {
    const datePart = (iv.eventDate || '').toString().split('T')[0];
    return {
      ...iv,
      type: 'interview',
      dateKey: datePart,
      localDate: parseDateKey(datePart),
      time: iv.eventTime || '',
      displayTitle: iv.applicant_name
        ? `${iv.applicant_name} - ${iv.job_title}`
        : iv.title || 'Interview',
      displaySubtitle: iv.company_name || 'Member'
    };
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [eventsRes, interviewsRes] = await Promise.all([
        fetch(buildApiUrl('/api/association/events'), { headers }),
        fetch(buildApiUrl('/api/association/interviews'), { headers })
      ]);
      const eventsData = eventsRes.ok ? (await eventsRes.json()) : { events: [] };
      const interviewsData = interviewsRes.ok ? (await interviewsRes.json()) : { interviews: [] };
      const evList = (eventsData.events || []).map(normalizeEvent).filter((e) => e.dateKey);
      const ivList = (interviewsData.interviews || []).map(normalizeInterview).filter((i) => i.dateKey);
      setEvents(evList);
      setInterviews(ivList);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to load calendar data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, []);

  useEffect(() => {
    const handleRefresh = () => fetchCalendarData();
    window.addEventListener('associationEventsUpdated', handleRefresh);
    return () => window.removeEventListener('associationEventsUpdated', handleRefresh);
  }, []);

  const allItems = useMemo(() => {
    const combined = [
      ...events.map((e) => ({ ...e, sortKey: e.dateKey + (e.time || '') })),
      ...interviews.map((i) => ({ ...i, sortKey: i.dateKey + (i.time || '') }))
    ];
    return combined.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [events, interviews]);

  const filteredItems = useMemo(() => {
    if (itemTypeFilter === 'events') return events;
    if (itemTypeFilter === 'interviews') return interviews;
    return allItems;
  }, [itemTypeFilter, events, interviews, allItems]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const getItemsForDate = (day) => {
    const targetKey = formatDateKey(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
    );
    return filteredItems.filter((item) => (item.dateKey || item.eventDate) === targetKey);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleDayClick = (day) => {
    const items = getItemsForDate(day);
    if (items.length === 1) {
      setSelectedItem(items[0]);
      setShowDetailModal(true);
    }
  };

  const handleItemClick = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const headers = weekDays.map((day) => (
      <div key={day} className="assoc-cal-header-day">{day}</div>
    ));

    const cells = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      cells.push(<div key={`empty-${i}`} className="assoc-cal-day assoc-cal-day-empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      currentDay.setHours(0, 0, 0, 0);
      const dayItems = getItemsForDate(day);
      const isToday = currentDay.getTime() === today.getTime();
      const isPast = currentDay < today;

      cells.push(
        <div
          key={day}
          className={`assoc-cal-day ${isToday ? 'assoc-cal-day-today' : ''} ${isPast ? 'assoc-cal-day-past' : ''} ${dayItems.length > 0 ? 'assoc-cal-day-has-items' : ''}`}
          onClick={() => handleDayClick(day)}
          onMouseEnter={() => dayItems.length > 0 && setHoveredDay(day)}
          onMouseLeave={() => setHoveredDay(null)}
        >
          <div className="assoc-cal-day-num">{day}</div>
          {dayItems.length > 0 && hoveredDay === day && (
            <div className="assoc-cal-tooltip">
              {dayItems.slice(0, 5).map((item, idx) => (
                <div
                  key={idx}
                  className="assoc-cal-tooltip-item"
                  onClick={(ev) => handleItemClick(item, ev)}
                >
                  <span className={`assoc-cal-type-badge assoc-cal-type-${item.type}`}>
                    {item.type === 'event' ? 'Event' : 'Interview'}
                  </span>
                  <span className="assoc-cal-tooltip-time">
                    {formatTimeValue(item.time) || 'All day'}
                  </span>
                  <div className="assoc-cal-tooltip-title">{item.displayTitle}</div>
                </div>
              ))}
              {dayItems.length > 5 && (
                <div className="assoc-cal-tooltip-more">+{dayItems.length - 5} more</div>
              )}
            </div>
          )}
          <div className="assoc-cal-day-items">
            {dayItems.slice(0, 3).map((item, idx) => (
              <button
                key={idx}
                type="button"
                className={`assoc-cal-item-card assoc-cal-item-${item.type}`}
                onClick={(ev) => handleItemClick(item, ev)}
              >
                <span className="assoc-cal-item-time">
                  {formatTimeValue(item.time) || '—'}
                </span>
                <span className="assoc-cal-item-title" title={item.displayTitle}>
                  {item.type === 'event' ? item.displayTitle : item.applicant_name || item.displayTitle}
                </span>
              </button>
            ))}
            {dayItems.length > 3 && (
              <div className="assoc-cal-more">+{dayItems.length - 3}</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="assoc-cal-grid">
        {headers}
        {cells}
      </div>
    );
  };

  const upcomingItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return allItems
      .filter((item) => item.localDate && item.localDate >= today)
      .sort((a, b) => {
        const aKey = (a.dateKey || '') + (a.time || '');
        const bKey = (b.dateKey || '') + (b.time || '');
        return aKey.localeCompare(bKey);
      })
      .slice(0, 8);
  }, [allItems]);

  if (loading) {
    return (
      <ThemedLoadingSpinner
        theme="recruiter"
        size="large"
        text="Loading calendar..."
        fullScreen={false}
      />
    );
  }

  return (
    <div className="assoc-calendar-container">
      <div className="assoc-calendar-header">
        <div className="assoc-calendar-title">
          <FontAwesomeIcon icon={faCalendarAlt} />
          <h1>Calendar</h1>
        </div>
        <p className="assoc-calendar-subtitle">
          View events and interviews at a glance
        </p>
        <div className="assoc-calendar-actions">
          <select
            value={itemTypeFilter}
            onChange={(e) => setItemTypeFilter(e.target.value)}
            className="assoc-cal-filter-select"
          >
            <option value="all">All</option>
            <option value="events">Events only</option>
            <option value="interviews">Interviews only</option>
          </select>
        </div>
      </div>

      <div className="assoc-calendar-layout">
        <div className="assoc-cal-main">
          <div className="assoc-cal-controls">
            <button type="button" className="assoc-cal-nav-btn" onClick={() => navigateMonth(-1)}>
              <span>‹</span>
            </button>
            <h2 className="assoc-cal-month-title">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button type="button" className="assoc-cal-nav-btn" onClick={() => navigateMonth(1)}>
              <span>›</span>
            </button>
            <button type="button" className="assoc-cal-today-btn" onClick={goToToday}>
              Today
            </button>
          </div>
          {error && <div className="assoc-cal-error">{error}</div>}
          <div className="assoc-cal-wrapper">{renderCalendar()}</div>
        </div>

        <div className="assoc-cal-sidebar">
          <div className="assoc-cal-sidebar-card">
            <h3>
              <FontAwesomeIcon icon={faCalendarAlt} />
              Upcoming
            </h3>
            <div className="assoc-cal-upcoming-list">
              {upcomingItems.length > 0 ? (
                upcomingItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="assoc-cal-upcoming-item"
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="assoc-cal-upcoming-date">
                      <span className="day">
                        {item.localDate ? item.localDate.getDate() : '—'}
                      </span>
                      <span className="month">
                        {item.localDate
                          ? item.localDate.toLocaleDateString('en-US', { month: 'short' })
                          : ''}
                      </span>
                    </div>
                    <div className="assoc-cal-upcoming-details">
                      <span className={`type-badge type-${item.type}`}>
                        {item.type === 'event' ? 'Event' : 'Interview'}
                      </span>
                      <span className="title">{item.displayTitle}</span>
                      <span className="time">{formatTimeValue(item.time) || 'All day'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="assoc-cal-empty">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <p>No upcoming events or interviews</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDetailModal && selectedItem && (
        <div
          className="assoc-cal-modal-overlay"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedItem(null);
          }}
        >
          <div
            className="assoc-cal-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="assoc-cal-modal-header">
              <h2>
                <span
                  className={`assoc-cal-modal-type-badge assoc-cal-type-${selectedItem.type}`}
                >
                  {selectedItem.type === 'event' ? 'Event' : 'Interview'}
                </span>
                {selectedItem.displayTitle}
              </h2>
              <button
                type="button"
                className="assoc-cal-modal-close"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedItem(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="assoc-cal-modal-body">
              {selectedItem.type === 'event' ? (
                <>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Date</span>
                    <span>
                      {selectedItem.localDate
                        ? selectedItem.localDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : '—'}
                    </span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Time</span>
                    <span>{formatTimeValue(selectedItem.time) || 'All day'}</span>
                  </div>
                  {selectedItem.eventType && (
                    <div className="assoc-cal-modal-row">
                      <span className="label">Type</span>
                      <span>{selectedItem.eventType}</span>
                    </div>
                  )}
                  {selectedItem.location && (
                    <div className="assoc-cal-modal-row">
                      <span className="label">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> Location
                      </span>
                      <span>{selectedItem.location}</span>
                    </div>
                  )}
                  {selectedItem.description && (
                    <div className="assoc-cal-modal-row full">
                      <span className="label">Description</span>
                      <p>{selectedItem.description}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Candidate</span>
                    <span>{selectedItem.applicant_name}</span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Position</span>
                    <span>{selectedItem.job_title}</span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Company</span>
                    <span>{selectedItem.company_name}</span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Date</span>
                    <span>
                      {selectedItem.localDate
                        ? selectedItem.localDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : '—'}
                    </span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Time</span>
                    <span>
                      {formatTimeValue(selectedItem.time) || formatTimeValue(selectedItem.endTime) || '—'}
                    </span>
                  </div>
                  <div className="assoc-cal-modal-row">
                    <span className="label">Mode</span>
                    <span>
                      {selectedItem.interview_mode === 'online' ? (
                        <FontAwesomeIcon icon={faVideo} />
                      ) : (
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      )}{' '}
                      {selectedItem.interview_mode}
                    </span>
                  </div>
                  {selectedItem.interview_link && (
                    <div className="assoc-cal-modal-row full">
                      <span className="label">Meeting Link</span>
                      <a
                        href={selectedItem.interview_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedItem.interview_link}
                      </a>
                    </div>
                  )}
                  {selectedItem.interview_location && (
                    <div className="assoc-cal-modal-row full">
                      <span className="label">Location</span>
                      <span>{selectedItem.interview_location}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssociationCalendar;
